import React, { useState } from "react"
import { Tooltip } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCopy, faCheck } from "@fortawesome/free-solid-svg-icons"

interface HashChipProps {
  /** The full hash. An empty value renders "none". */
  hash: string
  /** Characters of the prefix to show (default 8). */
  length?: number
  /** Optional label rendered before the hash (e.g. "file", "version ID"). */
  label?: string
  className?: string
}

/**
 * A hash as a compact, copyable chip: monospace prefix, the full value in the tooltip, click to
 * copy. Raw hashes are for checking, not reading: the chip keeps them out of the way while making
 * the full value one click away.
 */
export const HashChip: React.FC<HashChipProps> = ({ hash, length = 8, label, className = "" }) => {
  const [copied, setCopied] = useState(false)

  if (!hash) return <span className="text-slate-300">none</span>

  const copy = async (e: React.MouseEvent): Promise<void> => {
    e.stopPropagation()
    try {
      await navigator.clipboard.writeText(hash)
      setCopied(true)
      setTimeout(() => setCopied(false), 1200)
    } catch {
      /* clipboard unavailable (non-secure context) — the tooltip still shows the full value */
    }
  }

  return (
    <Tooltip title={copied ? "Copied!" : hash} overlayStyle={{ maxWidth: 560 }}>
      <button
        type="button"
        onClick={copy}
        aria-label={`Copy hash ${hash.slice(0, length)}`}
        className={`group inline-flex items-center gap-1 rounded border border-slate-200 bg-slate-50 px-1.5 py-px text-[10px] text-slate-500 transition-colors hover:border-slate-300 hover:bg-white ${className}`}
      >
        {label && <span className="text-slate-400">{label}</span>}
        <span className="font-mono">{hash.slice(0, length)}…</span>
        <FontAwesomeIcon
          icon={copied ? faCheck : faCopy}
          className={`h-2.5 w-2.5 ${copied ? "text-emerald-500" : "text-slate-300 group-hover:text-slate-400"}`}
        />
      </button>
    </Tooltip>
  )
}

export default HashChip
